import React, { useEffect, useContext } from 'react'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'

import { useGetFreightLanesQuery, useGetFreightLaneLocationsQuery } from 'services/freightLanes'

import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import FreightLanesGrid from 'components/generic/datagrid/FreightLanesGrid'

import { setDecimalPrecision } from 'utils/helpers'
import { dateFormatter, dateUnixFormatter, formatCityState } from 'utils/data-manipulation/valueFormatter'
import { FREIGHT_ACTIONS } from 'utils/ui/actions'

const FreightLanesContainer = () => {
  const context = useContext(ContentLayoutContext)

  const { defaultYardUm } = useSelector(state => state.settings)

  const { data: freightLanesData, isLoading: isLoadingFreightLanesData } = useGetFreightLanesQuery({})
  const { data: freightLaneLocationsData, isLoading: isLoadingFreightLaneLocationsData } =
    useGetFreightLaneLocationsQuery({})

  const isLoading = isLoadingFreightLanesData || isLoadingFreightLaneLocationsData

  const transformedRows = (freightLanesData?.Items || [])
    .slice()
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .map(freightLane => {
      const fuelSurcharge = freightLane.activePrice?.fuelSurcharge || 0
      const effectiveDate = dateUnixFormatter(freightLane.activePrice?.effectiveDate)
      const expirationDate = dateUnixFormatter(freightLane.activePrice?.expirationDate)
      return {
        id: freightLane.id,
        carrier: freightLane.carrier?.displayName || '',
        carrierId: freightLane.carrier?.id || '',
        location: formatCityState(freightLane.location),
        city: freightLane.location?.city || '',
        state: freightLane.location?.state || '',
        locationId: freightLane.location?.id || '',
        transportType: freightLane.transportationMode || '',
        transportTypeId: (freightLane.transportationMode || '').toUpperCase(),
        minCost: freightLane.activePrice?.minCost,
        costPerUm: freightLane.activePrice?.costPerUm,
        um: freightLane.activePrice?.um || defaultYardUm,
        fuelSurcharge,
        fuelSurchargeString: `${setDecimalPrecision(fuelSurcharge, { decimalPrecisionOverride: 1 })}%`,
        effectiveDate,
        effectiveDateString: dateFormatter(effectiveDate),
        expirationDate,
        expirationDateString: dateFormatter(expirationDate),
      }
    })

  const locationOptions = (freightLaneLocationsData?.Items || []).slice().map(location => ({
    id: location.id,
    name: formatCityState(location),
  }))

  useEffect(() => {
    context.setContainerTitle('Freight Lanes')
    context.setContainerActions(FREIGHT_ACTIONS.filter(action => action.id === 'add-freight-lane-button'))
  }, [])

  return (
    <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
      <FreightLanesGrid rows={transformedRows} loading={isLoading} locationOptions={locationOptions} />
    </Grid>
  )
}

export default FreightLanesContainer
